// Rift Mortar presentation (owner 2026-11-01; P1 concept 'Ember Core Cannon': dense, fast, destructive compressed
// artillery, distinct from the huge implosive Zenith Orb). The hero pushes both hands forward; two energy streams meet
// between the palms, a compact sphere swells (radius curve below), its surface rotates and veins tighten, the shell
// stabilises around a white-hot core, contracts briefly and launches with a directional burst. In flight it is a dense
// ember ball with a bright leading edge, spiral surface, short ragged tail and occasional sparks (not a beam). Impact is
// a reduced fiery blast (blastFx.mortarBurst) with a hot asymmetric petal flash.
// Render-only: reads `sim.mortar`, kind-7 projectiles and SimEvents. All pooled; no per-frame allocation.
import * as THREE from 'three/webgpu';
import { abs, atan, cos, dot, float, mix, mx_noise_float, normalize, normalView, oneMinus, positionLocal, positionViewDirection, sin, smoothstep, step, time, uniform, uv, vec3 } from 'three/tsl';
import type { Sim } from '../core/sim';
import { EnergyPalette, EnergyUniforms, flareMaterial } from './animeVfx';
import type { Fx } from './fx';
import { LANCE_POSES, ACTIVE_LANCE } from './heroPoses';
import type { HeroRig } from './models';

/** Hot centre and fiery structure always stay; only the outer shell / veins take the Surge colour (red II, blue III). */
// owner 2026-11-02: 'turn the rift mortar into a green blast' — white-hot core, lime glow, emerald shell, deep green rim
// (Surge II/III still recolour the shell red / blue)
const CORE = 0xf2fff0, HOT = 0xb8ff5a;
const PAL_FIRE: EnergyPalette = { core: new THREE.Color(CORE), hot: new THREE.Color(HOT), body: new THREE.Color(0x2fdc4a), rim: new THREE.Color(0x0a6a2a) };
const PAL_RED: EnergyPalette = { core: new THREE.Color(0xfff6f0), hot: new THREE.Color(0xffb0a0), body: new THREE.Color(0xff2a24), rim: new THREE.Color(0x7a0612) };
const PAL_BLUE: EnergyPalette = { core: new THREE.Color(0xf4fbff), hot: new THREE.Color(0xb0dcff), body: new THREE.Color(0x3a8cff), rim: new THREE.Color(0x10308e) };
const POOL = 6, PETALS = 4;
/** visual radius = sim sphere radius × VIS. Owner 2026-11-01 asked for a big charging fireball ('charge up from small to
 *  large'); at 0.8 it was a few pixels at the gameplay camera, so 1.5 (rank 1 ≈ 0.8 m radius). Render-only: hit and blast
 *  sizes are the sim's. */
const VIS = 1.5;
/** owner 2026-11-02: 'as you level up Rift Mortar, the charging ball, energy ball, and the explosion get larger' — extra
 *  visual growth per rank on top of the sim size (rank 1 ×1.0 → rank 5 ×1.5, evolved ×1.85) */
const rankK = (size: number) => 1 + Math.max(0, (size - 0.5) / 0.07) * 0.125;
/** P1 radius curve over the charge (normalised): small spark → swell → full → brief contraction before launch */
const CURVE: [number, number][] = [[0, 0.1], [0.2, 0.3], [0.4, 0.7], [0.6, 0.9], [0.8, 1], [0.95, 0.9], [1, 0.9]];
function radiusAt(k: number): number {
  for (let i = 1; i < CURVE.length; i++) if (k <= CURVE[i][0]) { const [a, ra] = CURVE[i - 1], [b, rb] = CURVE[i]; return ra + (rb - ra) * (k - a) / (b - a); }
  return 0.9;
}
const _v = new THREE.Vector3(), _v2 = new THREE.Vector3();

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Three-layer ember ball: ivory-hot centre, an uneven (displaced, churning) ember shell and thin rotating resonance
 *  seams. coreK 0..1 concentrates the white core, tight 0..1 tightens the veins, flight 1 adds the leading edge + spiral
 *  (the flying mesh is oriented with local +z along its velocity). */
function ballMaterial(u: EnergyUniforms, coreK: any, tight: any, flight: boolean): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const p = positionLocal, n = normalize(p);
  // uneven shell: lumpy, slowly churning displacement (not a smooth bubble)
  const lump = mx_noise_float(n.mul(2.2).add(vec3(time.mul(1.3), time.mul(-1.7), time.mul(0.9))));
  m.positionNode = p.mul(lump.mul(0.11).add(1));
  const fres = oneMinus(abs(dot(normalView, positionViewDirection)));
  const churn = mx_noise_float(n.mul(3.2).add(vec3(time.mul(2.4), time.mul(-3), time.mul(1.6))));
  const r = fres.add(churn.mul(0.18));
  const coreEdge = float(0.07).add(coreK.mul(0.13)); // the white-hot core concentrates as the charge stabilises
  const c0 = u.core.mul(1.5), c1 = u.hot.mul(1.1), c2 = u.body.mul(1.05), c3 = u.rim;
  let col: any = mix(c3, c2, step(r, float(0.74)));
  col = mix(col, c1, step(r, float(0.4)));
  col = mix(col, c0, step(r, coreEdge));
  // veins: thin ridges in the shell that tighten (higher frequency, thinner) as the charge builds
  const vf = float(3).add(tight.mul(4)), vn = oneMinus(abs(mx_noise_float(n.mul(vf).add(vec3(time.mul(5), time.mul(-4), time.mul(2.5))))));
  const vein = vn.pow(float(10).add(tight.mul(16))).mul(smoothstep(float(0.4), float(0.75), fres));
  col = mix(col, u.body.mul(2.2).add(c1.mul(0.4)), vein.clamp(0, 1));
  // resonance seams: two thin great circles rotating on tilted axes
  const t = time.mul(2.6);
  const ax1 = vec3(cos(t), float(0.35), sin(t)), ax2 = vec3(sin(t.mul(0.7)), cos(t.mul(0.7)), float(0.4));
  const seam = oneMinus(smoothstep(float(0.0), float(0.05), abs(dot(n, normalize(ax1))))).max(oneMinus(smoothstep(float(0.0), float(0.04), abs(dot(n, normalize(ax2))))));
  col = mix(col, u.body.mul(1.6).add(u.core.mul(0.6)), seam.mul(0.85).mul(step(float(0.3), r)));
  if (flight) {
    // spiral surface bands around the flight axis + a bright leading edge
    const spiral = sin(atan(n.y, n.x).mul(3).add(n.z.mul(9)).sub(time.mul(18))).mul(0.5).add(0.5);
    col = mix(col, c1, smoothstep(float(0.8), float(0.95), spiral).mul(step(float(0.35), r)).mul(0.6));
    col = mix(col, c0, smoothstep(float(0.45), float(0.95), n.z).mul(0.85));
  }
  m.colorNode = col.mul(u.intensity);
  m.opacityNode = float(0.97).sub(fres.pow(3).mul(0.2));
  return m;
}

/** Short ragged flame tail: open cone behind the ball (base at the ball, tip at -z), torn edges, fading to the tip. */
function tailMaterial(u: EnergyUniforms): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, fog: false });
  const along = uv().y; // 0 at the ball, 1 at the tip
  const rag = mx_noise_float(vec3(uv().x.mul(14), along.mul(5).sub(time.mul(12)), float(0.3))).mul(0.5).add(0.5);
  const fade = step(along.mul(1.1), rag.mul(0.9).add(0.15)).mul(oneMinus(along).pow(1.2));
  m.colorNode = mix(u.body, u.hot, oneMinus(along).pow(3)).mul(fade.mul(1.4));
  m.opacityNode = fade;
  return m;
}
function tailGeometry(): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(1, 1, 18, 1, true);
  g.rotateX(-Math.PI / 2); g.translate(0, 0, -0.5); // base ring at the origin, tip at z = -1
  return g;
}
/** Asymmetric impact petals: an irregular five-petal star, ivory centre to orange tips (vertex colours, no blending tricks
 *  so it reads with bloom off). */
function petalGeometry(): THREE.BufferGeometry {
  const P: number[] = [], C: number[] = [], ivory = new THREE.Color(0xf2fff0), tip = new THREE.Color(0x2fdc4a), mid = new THREE.Color(0xb8ff5a);
  const lens = [1, 0.62, 0.88, 0.5, 0.78], N = lens.length;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + (i % 2 ? 0.25 : -0.1), L = lens[i], w = 0.42 / N * Math.PI * 2;
    const ax = Math.cos(a), ay = Math.sin(a), lx = Math.cos(a - w), ly = Math.sin(a - w), rx = Math.cos(a + w), ry = Math.sin(a + w);
    P.push(0, 0, 0, lx * 0.38, ly * 0.38, 0, ax * L, ay * L, 0, 0, 0, 0, ax * L, ay * L, 0, rx * 0.38, ry * 0.38, 0);
    for (const c of [ivory, mid, tip, ivory, tip, mid]) C.push(c.r, c.g, c.b);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)); g.setAttribute('color', new THREE.Float32BufferAttribute(C, 3));
  return g;
}

interface Slot { ball: THREE.Mesh; halo: THREE.Mesh; tail: THREE.Mesh }
interface Petal { m: THREE.Mesh; op: any; t: number; life: number; size: number; rot: number }

export class MortarBall {
  readonly energy = new EnergyUniforms();
  /** 0..1 hands-forward pose weight (view.ts applies it to the hero) */
  pose = 0;
  /** the charging ball (visible) and each flying ball this frame, for the view's electricity: x, y, z, radius */
  readonly chargeBall = { on: false, x: 0, y: 0, z: 0, r: 0 };
  readonly flying: number[] = []; // flat [x, y, z, r, ...]
  /** world yaw of the aim while posing */
  aimYaw = 0;
  private charge: Slot; private slots: Slot[] = []; private petals: Petal[] = []; private nextPetal = 0;
  private coreK = uniform(0); private tight = uniform(0);
  private hold = 0; private releaseH = 1.2; private handBones: THREE.Object3D[] = []; private bonesOf: THREE.Object3D | null = null;
  private readonly hand = new THREE.Vector3(); private readonly handL = new THREE.Vector3(); private readonly handR = new THREE.Vector3();

  constructor(scene: THREE.Scene, private fx: Fx, private groundAt: (x: number, z: number) => number) {
    this.energy.set(PAL_FIRE, 1);
    const sphere = new THREE.SphereGeometry(1, 32, 24), plane = new THREE.PlaneGeometry(1, 1), tail = tailGeometry();
    const cm = ballMaterial(this.energy, this.coreK, this.tight, false), fm = ballMaterial(this.energy, float(1), float(1), true);
    const hm = flareMaterial(this.energy, 11, 5), tm = tailMaterial(this.energy);
    const mk = (bm: THREE.Material): Slot => {
      const s: Slot = { ball: new THREE.Mesh(sphere, bm), halo: new THREE.Mesh(plane, hm), tail: new THREE.Mesh(tail, tm) };
      s.halo.renderOrder = 3; s.tail.renderOrder = 3; s.ball.renderOrder = 4;
      for (const o of [s.ball, s.halo, s.tail]) { o.visible = false; o.frustumCulled = false; scene.add(o); }
      return s;
    };
    this.charge = mk(cm);
    for (let i = 0; i < POOL; i++) this.slots.push(mk(fm));
    const pg = petalGeometry();
    for (let i = 0; i < PETALS; i++) {
      const op = uniform(0), mat = new THREE.MeshBasicNodeMaterial({ vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
      mat.opacityNode = op;
      const m = new THREE.Mesh(pg, mat); m.visible = false; m.renderOrder = 5; m.frustumCulled = false; scene.add(m);
      this.petals.push({ m, op, t: 1, life: 1, size: 1, rot: 0 });
    }
  }

  /** 'mortar' event: launch with a directional burst from the hands */
  onFire(dx: number, dz: number, size: number): void {
    this.hold = 0.3; this.aimYaw = Math.atan2(dx, dz);
    const h = this.hand, R = size * VIS * 0.9 * rankK(size), fx = this.fx;
    this.releaseH = Math.max(R + 0.15, h.y - this.groundAt(h.x, h.z));
    const body = (this.energy.body.value as THREE.Color).getHex();
    fx.particles.exempt = true;
    for (let k = 0; k < 16; k++) { // forward cone of sparks + a few recoil wisps
      const s = 8 + Math.random() * 10, sx = (Math.random() - 0.5) * 0.9, sy = (Math.random() - 0.3) * 0.6;
      fx.particles.emitDir(h.x + dx * R, h.y, h.z + dz * R, (dx - dz * sx) * s, sy * s, (dz + dx * sx) * s, k % 3 ? HOT : k % 2 ? body : 0xffffff, 0.16 + Math.random() * 0.1, 0.18 + Math.random() * 0.1, 3);
    }
    for (let k = 0; k < 5; k++) { const a = Math.random() * 6.283; fx.particles.emitDir(h.x, h.y, h.z, Math.cos(a) * 2 - dx * 3, 0.5 + Math.random(), Math.sin(a) * 2 - dz * 3, body, 0.3, 0.25, 2); }
    fx.particles.exempt = false;
    fx.stars.add(h.x + dx * R, h.y, h.z + dz * R, 1.4 + R * 2.5, 0.1, CORE, 2.4);
  }

  /** 'mortarImpact' event: hot centre + asymmetric petals (0–0.1 s), then the reduced fiery blast; one short light. */
  impact(x: number, z: number, r: number, color: number): void {
    const gy = this.groundAt(x, z), fx = this.fx;
    const pe = this.petals[this.nextPetal]; this.nextPetal = (this.nextPetal + 1) % PETALS;
    pe.t = 0; pe.life = 0.13; pe.size = r * 0.75; pe.rot = Math.random() * 6.283;
    pe.m.position.set(x, gy + 0.9, z); pe.m.visible = true;
    fx.blast?.mortarBurst(x, z, r, color);
    fx.stars.add(x, gy + 0.8, z, r * 0.9, 0.12, CORE, 2.6);
    fx.particles.exempt = true;
    for (let k = 0; k < 14; k++) { // embers
      const a = Math.random() * 6.283, s = 3 + Math.random() * r * 1.5;
      fx.particles.emitDir(x + Math.cos(a) * 0.4, gy + 0.6, z + Math.sin(a) * 0.4, Math.cos(a) * s, 3 + Math.random() * 5, Math.sin(a) * s, k % 2 ? HOT : color, 0.12 + Math.random() * 0.08, 0.6 + Math.random() * 0.4, 2.4);
    }
    fx.particles.exempt = false;
    fx.lights.flash(x, gy + 0.5, z, HOT, 26, 0.16, 6 + r * 1.5);
    fx.shake = Math.min(1, fx.shake + 0.12 + r * 0.02);
  }

  /** Per frame. gltfRoot = the authored hero when it is the active model (else null: the procedural rig's forearms). */
  update(dt: number, sim: Sim, t: number, camQ: THREE.Quaternion, rig: HeroRig, gltfRoot: THREE.Object3D | null, hx: number, hy: number, hz: number): void {
    const p = sim.player, m = sim.mortar;
    const tier = 0; // owner 2026-11-02: the Mortar keeps its own green at every Surge level
    this.energy.set(tier >= 3 ? PAL_BLUE : tier >= 2 ? PAL_RED : PAL_FIRE, Math.min(1, dt * 8));
    this.energy.intensity.value = 1;
    this.hold = Math.max(0, this.hold - dt);
    const want = m.charging || this.hold > 0 ? 1 : 0;
    this.pose += (want - this.pose) * Math.min(1, dt * (want ? 18 : 7));
    if (m.charging) this.aimYaw = Math.atan2(p.aimX, p.aimZ);
    const ax = Math.sin(this.aimYaw), az = Math.cos(this.aimYaw);
    const body = (this.energy.body.value as THREE.Color).getHex();

    // petals: pop open fast, fade by 0.13 s
    for (const pe of this.petals) {
      if (!pe.m.visible) continue;
      pe.t += dt; const u = pe.t / pe.life;
      if (u >= 1) { pe.m.visible = false; continue; }
      pe.m.quaternion.copy(camQ); pe.m.rotateZ(pe.rot + u * 0.4); pe.m.scale.setScalar(pe.size * (0.55 + 0.6 * Math.sqrt(u)));
      pe.op.value = 1 - u * u;
    }

    // ---- charge ----
    const cs = this.charge;
    if (m.charging || this.pose > 0.05) this.handPos(rig, gltfRoot, hx, hy, hz, ax, az);
    if (m.charging) {
      const k = Math.min(1, m.t / Math.max(0.01, m.dur));
      const Rf = m.size * VIS * rankK(m.size), R = Rf * radiusAt(k) * (1 + Math.sin(t * 40) * 0.025 * k);
      this.coreK.value = Math.max(0, Math.min(1, (k - 0.55) / 0.35)); this.tight.value = Math.max(0, Math.min(1, (k - 0.2) / 0.4));
      const h = this.hand;
      _v.set(h.x + ax * Rf * 0.8, Math.max(h.y, this.groundAt(h.x, h.z) + R + 0.1), h.z + az * Rf * 0.8); // just ahead of the palms, never sunk into the ground
      cs.ball.visible = k > 0.08; cs.ball.position.copy(_v); cs.ball.scale.setScalar(R);
      Object.assign(this.chargeBall, { on: cs.ball.visible, x: _v.x, y: _v.y, z: _v.z, r: R });
      cs.ball.rotation.set(t * 2.1, t * 3.3 * (0.5 + k), 0); // surface rotation speeds up as it builds
      cs.halo.visible = true; cs.halo.position.copy(_v); cs.halo.quaternion.copy(camQ);
      cs.halo.scale.setScalar(R * (k < 0.2 ? 3.2 : 2.3) * (1 + Math.sin(t * 37) * 0.08));
      cs.tail.visible = false;
      const fx = this.fx; fx.particles.exempt = true;
      if (k < 0.3) {
        // two energy streams leave the palms and meet between them
        for (const src of [this.handL, this.handR]) for (let n = 0; n < 3; n++) {
          const s = Math.random(), ox = src.x + (_v.x - src.x) * s * 0.3, oy = src.y + 0.05 + (Math.random() - 0.5) * 0.1, oz = src.z + (_v.z - src.z) * s * 0.3, life = 0.12;
          fx.particles.emitDir(ox, oy, oz, (_v.x - ox) / life, (_v.y - oy) / life, (_v.z - oz) / life, n === 0 ? 0xffffff : HOT, 0.1 + Math.random() * 0.06, life, 4);
        }
      }
      // converging motes: start on a tight shell and die at the surface (fewer as the shell stabilises)
      const nIn = k < 0.6 ? 6 : 3;
      for (let n = 0; n < nIn; n++) {
        const u = Math.random() * 2 - 1, th = Math.random() * 6.283, s2 = Math.sqrt(1 - u * u), D = R + 0.8 + Math.random() * 1.4;
        const dx = s2 * Math.cos(th), dy = u * 0.7, dz = s2 * Math.sin(th), life = 0.16 + Math.random() * 0.12, sp = (D - R * 0.9) / life;
        fx.particles.emitDir(_v.x + dx * D, _v.y + dy * D, _v.z + dz * D, -dx * sp, -dy * sp, -dz * sp, n % 3 === 0 ? 0xffffff : n % 3 === 1 ? HOT : body, 0.08 + Math.random() * 0.08, life, 4);
      }
      // rim crackle: short sparks flicking off tangentially
      if (Math.random() < 0.7) {
        const a = Math.random() * 6.283, b = Math.random() * 2 - 1, s2 = Math.sqrt(1 - b * b), nx = s2 * Math.cos(a), ny = b, nz = s2 * Math.sin(a);
        fx.particles.emitDir(_v.x + nx * R, _v.y + ny * R, _v.z + nz * R, -nz * 5 + nx * 2, ny * 2 + (Math.random() - 0.5) * 3, nx * 5 + nz * 2, Math.random() < 0.5 ? 0xffffff : body, 0.08 + 0.05 * k, 0.1, 4);
      }
      fx.particles.exempt = false;
    } else { cs.ball.visible = false; cs.halo.visible = false; cs.tail.visible = false; this.chargeBall.on = false; }

    // ---- flight ----
    let n = 0; this.flying.length = 0;
    for (const pr of sim.projectiles) {
      if (!pr.alive || pr.hostile || pr.kind !== 7 || n >= POOL) continue;
      const s = this.slots[n++], R = pr.out * VIS * 0.9 * rankK(pr.out), sp = Math.hypot(pr.vx, pr.vz), dx = sp > 1e-3 ? pr.vx / sp : ax, dz = sp > 1e-3 ? pr.vz / sp : az;
      const y = this.groundAt(pr.x, pr.z) + Math.max(this.releaseH, R + 0.15); // big ball rides above the ground
      s.ball.visible = true; s.ball.position.set(pr.x, y, pr.z); s.ball.scale.setScalar(R); this.flying.push(pr.x, y, pr.z, R);
      _v2.set(pr.x + dx, y, pr.z + dz); s.ball.lookAt(_v2); // local +z = flight direction (leading edge + spiral)
      s.halo.visible = true; s.halo.position.copy(s.ball.position); s.halo.quaternion.copy(camQ); s.halo.scale.setScalar(R * (2 + Math.sin(t * 31 + n) * 0.12));
      s.tail.visible = true; s.tail.position.copy(s.ball.position); s.tail.lookAt(_v2);
      s.tail.scale.set(R * 0.85, R * 0.85, R * (1.7 + Math.sin(t * 23 + n) * 0.3));
      const fx = this.fx; fx.particles.exempt = true;
      { const a = Math.random() * 6.283; fx.particles.emitDir(pr.x - dx * R + Math.cos(a) * R * 0.5, y + Math.sin(a) * R * 0.5, pr.z - dz * R, -dx * 4, 0.5, -dz * 4, body, R * 0.5, 0.15, 2.4); }
      if (Math.random() < 0.3) { const a = Math.random() * 6.283; fx.particles.emitDir(pr.x, y, pr.z, Math.cos(a) * 5 - dx * 3, 2 + Math.random() * 3, Math.sin(a) * 5 - dz * 3, Math.random() < 0.5 ? 0xffffff : HOT, 0.08, 0.3, 3); }
      fx.particles.exempt = false;
    }
    for (let i = n; i < POOL; i++) { const s = this.slots[i]; s.ball.visible = false; s.halo.visible = false; s.tail.visible = false; }
  }

  /** Hands-forward pose for the procedural rig: the approved Nova Lance release key (two palms forward), arms only, so
   *  the legs keep their walk cycle. Reuses existing pose data; no new animation. */
  poseRig(rig: HeroRig): void {
    const w = this.pose; if (w <= 0.01) return;
    const k = LANCE_POSES[ACTIVE_LANCE].thrust, a = rig.armR.rotation, b = rig.armL.rotation;
    a.set(a.x + (k.armR[0] - a.x) * w, a.y + (k.armR[1] - a.y) * w, a.z + (k.armR[2] - a.z) * w);
    b.set(b.x + (k.armL[0] - b.x) * w, b.y + (k.armL[1] - b.y) * w, b.z + (k.armL[2] - b.z) * w);
    rig.foreR.rotation.x += (k.foreR - rig.foreR.rotation.x) * w; rig.foreL.rotation.x += (k.foreL - rig.foreL.rotation.x) * w;
  }

  /** both hands in world space (authored hero: hand bones; procedural: forearm tips) and their midpoint */
  private handPos(rig: HeroRig, gltfRoot: THREE.Object3D | null, hx: number, hy: number, hz: number, ax: number, az: number): void {
    const h = this.hand, L = this.handL, R = this.handR;
    if (gltfRoot) {
      if (this.bonesOf !== gltfRoot) {
        this.bonesOf = gltfRoot; this.handBones.length = 0;
        gltfRoot.traverse((o) => { if ((o as THREE.Bone).isBone && /^hand\.?[LR]$/.test(o.name)) this.handBones.push(o); });
      }
      if (this.handBones.length === 2) {
        gltfRoot.updateMatrixWorld(true);
        this.handBones[0].getWorldPosition(L); this.handBones[1].getWorldPosition(R);
        h.copy(L).add(R).multiplyScalar(0.5); h.x += ax * 0.1; h.z += az * 0.1;
        return;
      }
    } else {
      rig.root.updateMatrixWorld(true);
      rig.foreL.localToWorld(L.set(0, -0.36, 0.06)); rig.foreR.localToWorld(R.set(0, -0.36, 0.06)); h.copy(L).add(R).multiplyScalar(0.5);
      return;
    }
    h.set(hx + ax * 0.7, hy + 0.35, hz + az * 0.7); L.copy(h); R.copy(h);
  }

  clear(): void {
    this.charge.ball.visible = this.charge.halo.visible = this.charge.tail.visible = false;
    for (const s of this.slots) s.ball.visible = s.halo.visible = s.tail.visible = false;
    for (const pe of this.petals) pe.m.visible = false;
    this.pose = 0; this.hold = 0;
  }
}
